import {
  FC,
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react'
import { useAppContext, type AppContextType } from './app-context'

export type FilterKey = 'region' | 'military' | 'year'
export type FilterSubkey = 'month' | 'subregion'
export type AllFilters = FilterKey | FilterSubkey

type FiltersContextType = {
  search: string
  setSearch: React.Dispatch<React.SetStateAction<string>>
  filteredData: AppContextType['data']
  options: {
    [k in 'military' | 'year']: { [n: string]: number }
  } & {
    region: {
      [k: string]: {
        count: number
        value: string
        subregions: { [n: string]: number }
      }
    }
  }
  filters: Partial<{ [k in AllFilters]: string }>
  setFilters: React.Dispatch<
    React.SetStateAction<Partial<{ [k in AllFilters]: string }>>
  >
}

const FiltersContext = createContext<FiltersContextType | null>(null)

export const FiltersContextProvider: FC<{
  children?: ReactNode | ReactNode[]
}> = ({ children }) => {
  const { data } = useAppContext()
  const [filteredData, setFilteredData] =
    useState<FiltersContextType['filteredData']>(data)
  const [filters, setFilters] = useState<FiltersContextType['filters']>({})
  const [search, setSearch] = useState('')
  const [options, setOptions] = useState<FiltersContextType['options']>({
    region: {},
    military: {},
    year: {},
  })

  const updateFilters = () => {
    if (data) {
      setOptions({
        region: data.reduce(
          (acc, item) =>
            !item.region
              ? acc
              : acc[item.region]
              ? {
                  ...acc,
                  [item.region]: {
                    ...acc[item.region],
                    count: acc[item.region].count + 1,
                    subregions: {
                      ...acc[item.region].subregions,
                      [item.subregion]:
                        (acc[item.region].subregions[item.subregion] ?? 0) + 1,
                    },
                  },
                }
              : {
                  ...acc,
                  [item.region]: {
                    count: 1,
                    value: item.region_map,
                    subregions: {
                      [item.subregion]: 1,
                    },
                  },
                },
          {} as typeof options.region
        ),
        military: data.reduce(
          (acc, item) =>
            !item.military
              ? acc
              : acc[item.military]
              ? {
                  ...acc,
                  [item.military]: acc[item.military] + 1,
                }
              : {
                  ...acc,
                  [item.military]: 1,
                },
          {} as typeof options.military
        ),
        year: data.reduce((acc, item) => {
          try {
            const year = !/^[^\d\s.]+$/.test(item.date_of_death)
              ? item.date_of_death.match(/[0-9]{4}/)?.[0]
              : item.date_of_death

            return !year
              ? acc
              : acc[year]
              ? {
                  ...acc,
                  [year]: acc[year] + 1,
                }
              : {
                  ...acc,
                  [year]: 1,
                }
          } catch (err) {
            console.log(item.date_of_death, 'err')
            return acc
          }
        }, {} as typeof options.year),
      })
    }
  }

  useEffect(() => {
    updateFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    setFilteredData(
      data
        ?.filter(item =>
          Object.keys(filters).every(key => {
            if (
              !filters[key as keyof typeof filters] ||
              key === 'month' ||
              key === 'subregion'
            ) {
              return true
            }

            if (key === 'region') {
              return (
                (item.region === filters.region ||
                  item.region_map === filters.region) &&
                (!filters.subregion || filters.subregion === item.subregion)
              )
            }

            if (key === 'year') {
              return item.date_of_death
                .toString()
                .includes(
                  `${filters.month ? `${filters.month}.` : ''}${filters.year}`
                )
            }

            return (
              item[key as keyof typeof item] ===
              filters[key as keyof typeof filters]
            )
          })
        )
        .filter(item => {
          const searchRegExp = new RegExp(`^(.+[ ])?${search.trim()}`, 'ig')
          return !search || searchRegExp.test(item.name)
        }) ?? null
    )
  }, [data, filters, search])

  return (
    <FiltersContext.Provider
      value={{ filters, setFilters, filteredData, options, search, setSearch }}
    >
      {children}
    </FiltersContext.Provider>
  )
}

export const useFiltersContext = () => {
  const context = useContext(FiltersContext)
  return context as FiltersContextType
}
